//
// query/n1ql.ts
//
// Copyright 2025-Present Couchbase, Inc.
//
// Use of this software is governed by the Business Source License included
// in the file licenses/BSL-Couchbase.txt.  As of the Change Date specified
// in that file, in accordance with the Business Source License, use of this
// software will be governed by the Apache License, Version 2.0, included in
// the file licenses/APL2.txt.
//

import { VisitExpr } from './normalize.js';
import Grammar, {type N1QLSemantics} from './N1QL.ohm-bundle.js'; // generated by `bun run build-grammar`
import type { DataSource, Expr, ExprDict, JoinSource, JoinType, Operation, Select } from "./schema";
import type { IterationNode, Node, NonterminalNode, TerminalNode } from 'ohm-js';


/** Exception thrown by syntax or semantic errors in a N1QL query. */
export class N1QLParseError extends Error {
    constructor(message: string);
    constructor(message: string, sourceStart: number, sourceEnd: number);
    /** @internal */
    constructor(message: string, sourceOperation: Operation);

    constructor(message: string, arg?: number | Operation, arg2?: number) {
        super(message);
        this.name = "N1QLParseError";
        if (typeof arg === 'number')
            this.sourceRange = [arg, arg2 ?? arg];
        else if (Array.isArray(arg) && arg.sourceTextStart !== undefined)
            this.sourceRange = [arg.sourceTextStart, arg.sourceTextEnd ?? arg.sourceTextStart];
    }

    sourceRange?: [number, number];
}


/** Parses a N1QL "SELECT" statement to JSON. */
export function ParseSelect(n1ql: string): Select {
    return Parse<Select>(n1ql, "SelectStatement");
}


/** Parses a N1QL expression to JSON. */
export function ParseExpression(n1ql: string): Expr {
    return Parse<Expr>(n1ql, "Expression");
}


function Parse<T>(n1ql: string, rule: string): T {
    InitSemantics();
    let match = Grammar.match(n1ql,rule);
    if (match.failed())
        throw new N1QLParseError(match.shortMessage!, match.getInterval().startIdx, match.getInterval().endIdx);
    // eslint-disable-next-line @typescript-eslint/no-unsafe-call
    return gSemantics(match).json() as T;
}


/* eslint-disable camelcase */  // Ohm likes to use underscores


let gSemantics!: N1QLSemantics;


function InitSemantics() {
    if (gSemantics !== undefined)
        return;

    gSemantics = Grammar.createSemantics();

    function attachSourceRange(expr: Expr, node: Node): Expr {
        if (Array.isArray(expr)) {
            expr.sourceTextStart = node.source.startIdx;
            expr.sourceTextEnd   = node.source.endIdx;
        }
        return expr;
    }

    // workaround for TypeScript not being able to type-check the dynamically-added `.json()` method
    function json(node: NonterminalNode | TerminalNode): Expr {
        // eslint-disable-next-line @typescript-eslint/no-unsafe-call
        return attachSourceRange(node.json() as Expr, node);
    }

    function childrenAsJson(node: IterationNode): Expr[] {
        return node.children.map(json);
    }

    // Returns an Ohm semantic function that produces a JSON binary Expr.
    function binaryOp(op: string) {
        return function(this: NonterminalNode,
                        lhs: NonterminalNode, _op: TerminalNode,
                        rhs: NonterminalNode): Expr {
            return [op, json(lhs), json(rhs)];
        };
    }


    gSemantics.addOperation<Expr>("json()", {
        // Select:

        SelectStatement(_sel, distinct, results, from,
                        _where, where, group, having,
                        order, limit, _semi): Expr
        {
            let result: Select = {
                WHAT: childrenAsJson(results.asIteration()),
                FROM: []
            };
            if (from.numChildren > 0)
                result.FROM = json(from.child(0)) as unknown as DataSource[];
            if (distinct.numChildren > 0)
                result.DISTINCT = true;
            if (where.numChildren > 0)
                result.WHERE = json(where.child(0));
            if (group.numChildren > 0)
                result.GROUP_BY = childrenAsJson(group.child(0).child(2).asIteration());
            if (having.numChildren > 0 && having.child(0).numChildren > 0)
                result.HAVING = json(having.child(0).child(0).child(1));
            if (order.numChildren > 0)
                result.ORDER_BY = childrenAsJson(order.child(0).child(2).asIteration());
            if (limit.numChildren > 0) {
                let [_, off, lim] = json(limit.child(0)) as Operation;
                if (off !== null)
                    result.OFFSET = off;
                if (lim !== null)
                    result.LIMIT = lim;
            }
            return result as unknown as Expr;
        },

        SelectResult(expr, _as, alias): Expr {
            let result: Expr = json(expr);
            if (alias.numChildren > 0)
                result = ["AS", result, json(alias.child(0))];
            return result;
        },

        // Kludge: returns DataSource[], but it has to be typed as Expr
        FromClause(_from, from: NonterminalNode, joins: IterationNode) {
            const fromSrc = json(from);
            if (joins.numChildren === 0)
                return [fromSrc] as Expr;
            else
                return [fromSrc, ... childrenAsJson(joins)] as unknown as Expr;
        },

        // Kludge: returns FromSource[], but it has to be typed as Expr
        CollectionSource(name1: NonterminalNode, _dot, name2: IterationNode, _as, alias: IterationNode): Expr {
            let result: DataSource;
            if (name2.numChildren > 0)
                result = {SCOPE: json(name1) as string, COLLECTION: json(name2.child(0)) as string};
            else
                result = {COLLECTION: json(name1) as string};
            if (alias.numChildren > 0)
                result.AS = json(alias.child(0)) as string;
            return result as unknown as Expr;
        },

        // Kludge: returns JoinSource[], but it has to be typed as Expr
        Join(type, _join, source: NonterminalNode, _on, on): Expr {
            const result = json(source) as unknown as JoinSource;
            if (type.numChildren > 0)
                result.JOIN = json(type.child(0)) as JoinType;
            else
                result.JOIN = "INNER";
            result.ON = json(on);
            return result as unknown as Expr;
        },

        JoinType_inner(_): Expr     {return "INNER";},
        JoinType_outer(_, __): Expr {return "OUTER";},
        JoinType_left(_, __): Expr  {return "LEFT OUTER";},
        JoinType_cross(_): Expr     {return "CROSS";},

        // Kludge: returns UnnestSource[], but it has to be typed as Expr
        Unnest(_, expr: NonterminalNode, _as, alias: IterationNode): Expr {
            const result: DataSource = {UNNEST: json(expr)};
            if (alias.numChildren > 0)
                result.AS = json(alias.child(0)) as string;
            return result as unknown as Expr;
        },

        Ordering(exp, order): Expr {
            let result = json(exp);
            if (order.numChildren > 0 && order.child(0).sourceString.toUpperCase() === "DESC")
                result = ["DESC", result];
            return result;
        },

        LimitOffset_limitFirst(_limit, limit, _offset, offset) {
            let result: Expr = ['limitoffset', null, json(limit)]; //Kludge: fake operator
            if (offset.numChildren > 0)
                result[1] = json(offset.child(0));
            return result;
        },
        LimitOffset_offsetFirst(_offset, offset, _limit, limit) {
            let result: Expr = ['limitoffset', json(offset), null]; //Kludge: fake operator
            if (limit.numChildren > 0)
                result[2] = json(limit.child(0));
            return result;
        },

        // Expressions:

        OrExp_or:           binaryOp("OR"),
        AndExp_and:         binaryOp("AND"),

        EqExp_eq:           binaryOp("="),
        EqExp_neq:          binaryOp("!="),

        EqExp_isValued(lhs, _is, notx, _valued): Expr {
            let result: Operation = ['IS VALUED', json(lhs)];
            if (notx.numChildren > 0)
                result = ['NOT', result];
            return result;
        },

        EqExp_isNot(lhs, _, __, rhs): Expr      {return ["IS NOT", json(lhs), json(rhs)];},

        EqExp_is:           binaryOp("IS"),
        EqExp_like:         binaryOp("LIKE"),
        EqExp_notLike(lhs, _not, _op, rhs): Expr {return ["NOT", ["LIKE", json(lhs), json(rhs)]];},

        EqExp_notNull(lhs, _not, _null): Expr   {return ['IS NOT', lhs, null];},

        EqExp_inExpr(lhs, inOrNot, rhs): Expr {
            return [inOrNot.sourceString.toUpperCase(), json(lhs), json(rhs)];
        },

        EqExp_inArray(lhs, inOrNot, array): Expr {
            return [inOrNot.sourceString.toUpperCase(), json(lhs), json(array)];
        },

        EqExp_between(lhs, not, _, min, __, max): Expr {
            let result: Operation = ["BETWEEN", json(lhs), json(min), json(max)];
            if (not.numChildren > 0)
                result = ["NOT", result];
            return result;
        },

        RelExp_rel(lhs, op, rhs): Expr      {return [op.sourceString, json(lhs), json(rhs)]; },
        BitExp_bit(lhs, op, rhs): Expr      {return [op.sourceString, json(lhs), json(rhs)]; },

        AddExp_plus:        binaryOp("+"),
        AddExp_minus:       binaryOp("-"),
        MulExp_times:       binaryOp("*"),
        MulExp_divide:      binaryOp("/"),
        MulExp_modulo:      binaryOp("%"),
        ConcatExp_concat:   binaryOp("||"),

        PrimaryExp_paren(_lp, exp, _rp): Expr     {return json(exp); },
        PrimaryExp_pos(_, rhs): Expr              {return json(rhs);},
        PrimaryExp_neg(_, rhs): Expr              {return ["-", json(rhs)];},
        PrimaryExp_not(_, rhs): Expr              {return ["NOT", json(rhs)];},
        PrimaryExp_exists(_, _lp, sel, _rp): Expr {return ["EXISTS", json(sel)];},

        parameter(_, name): Expr            {return ["$", json(name)];},
        variable(_, name): Expr             {return ["?", json(name)];},

        AnyEveryExp(anyEv, varNameNode, _in, arrayNode, _sat, testNode, _end): Expr {
            const varName = json(varNameNode);
            const testExpr =  json(testNode);
            // Within testExpr, the identifier varName is a variable, not a property:
            VisitExpr(testExpr, (op, subexpr) => {
                if (op === '.' && subexpr[1] === varName)
                    subexpr[0] = '?';
                return true;
            });
            return [json(anyEv) as string, varName, json(arrayNode), testExpr];
        },
        AnyEvery_any(_): Expr               {return "ANY";},
        AnyEvery_every(_): Expr             {return "EVERY";},
        AnyEvery_anyEvery(_, __, ___): Expr {return "ANY AND EVERY";},

        CaseExp(_case, test, whenThen, _else, elseExpr, _end): Expr {
            const result: Operation = ["CASE", (test.numChildren > 0) ? json(test.child(0)) : null];
            for (const when of whenThen.children) {
                result.push(json(when.child(1)));
                result.push(json(when.child(3)));
            }
            if (elseExpr.numChildren > 0)
                result.push(json(elseExpr.child(0)));
            return result;
        },

        // Properties:

        Property_all(_): Expr               {return ["."];},

        Property_allInCollection(coll, _, __): Expr {
            const expr: Expr = [".", json(coll)];
            expr.starColumn = true;
            return expr;
        },

        PropertyPath(name, cont): Expr      {return [".", json(name), ...childrenAsJson(cont)]; },
        PropertyPathContinuation_named(_, name): Expr       {return json(name);},
        PropertyPathContinuation_indexed(_, idx, __): Expr  {return json(idx);},

        // Functions:

        MetaFunction_plain(_meta, _lp, alias, _rp) {
            const result: Operation = ['META()'];
            if (alias.numChildren > 0)
                result.push(json(alias.child(0)));
            return result;
        },

        MetaFunction_property(_meta, _lp, alias, _rp, _dot, prop) {
            const result: Operation = ['META()'];
            if (alias.numChildren > 0)
                result.push(json(alias.child(0)));
            else
                result.push(null);
            result.push(prop.sourceString.toLowerCase());
            return result;
        },

        N1QLFunction(name: NonterminalNode, _, args: NonterminalNode, __): Expr {
            const nameStr = (json(name) as string).toUpperCase() + '()';
            return [nameStr, ...childrenAsJson(args.asIteration())];
        },

        // Literals:

        ArrayLiteral(_, items, __): Expr    {return ['[]', ...childrenAsJson(items.asIteration())];},

        DictLiteral(_, items, __): Expr {
            const result: ExprDict = {};
            for (const item of items.asIteration().children)
                result[ json(item.child(0)) as string] = json(item.child(2));
            return result;
        },

        wholeNumber(_): Expr                {return parseInt(this.sourceString);},
        number(_): Expr                     {return parseFloat(this.sourceString);},
        ident_unquoted(_1, _2): Expr        {return this.sourceString;},
        ident_quoted(_1, str, _2): Expr     {return str.sourceString;},
        stringLiteral(_1, str, _2): Expr    {return str.sourceString;},
        literal_true(_): Expr               {return true;},
        literal_false(_): Expr              {return false;},
        literal_null(_): Expr               {return null;},
        literal_missing(_): Expr            {return ["MISSING"];},
    });
}
